import { ColumnState, GridApi } from '@ag-grid-community/core';
import { apiClient } from '@api/apiClient';
import { fetchSavedSearches } from '@store/loadboard/services';
import { SavedSearchCategory } from '@store/loadboard/types';
import { userStore } from '@store/user';
import { debounce } from 'lodash';

const CACHE_NAME = 'cache';

type CustomerGridCache = {
  filterModel: Record<string, unknown>;
  columnState: ColumnState[];
};

let isApplyingCache = false;

const saveCacheToServer = async (gridApi: GridApi) => {
  if (isApplyingCache) return;

  const filterModel = gridApi.getFilterModel();
  const columnState: ColumnState[] = gridApi.getColumnState();
  const completeState: CustomerGridCache = {
    filterModel,
    columnState,
  };

  try {
    await apiClient.post('/user/SaveSearchSettings', {
      userId: userStore.user.id,
      Category: SavedSearchCategory.Customers,
      Name: CACHE_NAME,
      Params: JSON.stringify(completeState),
    });
  } catch {
    // Silently fail - cache save is not critical
  }
};

export const saveCustomerCache = debounce((gridApi: GridApi) => {
  void saveCacheToServer(gridApi);
}, 500);

export const fetchCustomerCache = async (): Promise<
  CustomerGridCache | undefined
> => {
  try {
    const searches = await fetchSavedSearches();
    const cacheEntry = searches.find(
      (s) =>
        s.category === SavedSearchCategory.Customers && s.name === CACHE_NAME,
    );
    if (cacheEntry) {
      const parsed = JSON.parse(
        cacheEntry.params,
      ) as Partial<CustomerGridCache>;
      return {
        filterModel:
          parsed.filterModel && typeof parsed.filterModel === 'object'
            ? parsed.filterModel
            : {},
        columnState: Array.isArray(parsed.columnState)
          ? parsed.columnState
          : [],
      };
    }
  } catch {
    // Silently fail - cache fetch is not critical
  }
  return undefined;
};

export const applyCustomerCache = (
  gridApi: GridApi,
  cache: CustomerGridCache,
) => {
  isApplyingCache = true;

  try {
    if (cache.columnState.length > 0) {
      gridApi.applyColumnState({
        state: cache.columnState,
        applyOrder: true,
      });
    }

    if (Object.keys(cache.filterModel).length > 0) {
      gridApi.setFilterModel(cache.filterModel);
    }
  } finally {
    isApplyingCache = false;
  }
};
